import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, Mail, Mic, Send, Sparkles } from 'lucide-react';
import type { Client, Task, User as UserEntity, Website } from '@oneup/shared';
import {
  clientsApi,
  communicationsApi,
  tasksApi,
  usersApi,
  websitesApi,
  type GmailSendAsAlias,
} from '@/lib/api';
import type { EmailComposerInitial } from '@/stores/ui';
import { useUiStore } from '@/stores/ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormDrawer } from '@/components/form-drawer';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface EmailComposerModalProps {
  open: boolean;
  initial?: EmailComposerInitial;
  onOpenChange: (open: boolean) => void;
}

interface ComposerState {
  clientSearch: string;
  contactSearch: string;
  taskSearch: string;
  websiteSearch: string;
  selectedClient?: Client;
  selectedContact?: UserEntity;
  selectedTask?: Task;
  selectedWebsite?: Website;
  fromEmail: string;
  toEmailsText: string;
  ccEmailsText: string;
  subject: string;
  bodyMarkdown: string;
  aiInstruction: string;
}

const EMPTY_STATE: ComposerState = {
  clientSearch: '',
  contactSearch: '',
  taskSearch: '',
  websiteSearch: '',
  fromEmail: '',
  toEmailsText: '',
  ccEmailsText: '',
  subject: '',
  bodyMarkdown: '',
  aiInstruction: '',
};

export function EmailComposerModal({ open, initial, onOpenChange }: EmailComposerModalProps) {
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<ComposerState>(EMPTY_STATE);
  const [sendError, setSendError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    if (!open) return;
    const subject = initial?.subject
      ? initial.mode === 'reply' && !/^re:/i.test(initial.subject)
        ? `Re: ${initial.subject}`
        : initial.subject
      : '';
    setState({
      ...EMPTY_STATE,
      clientSearch: initial?.clientName ?? '',
      taskSearch: initial?.taskName ?? '',
      selectedClient:
        initial?.clientId && initial.clientName
          ? ({ id: initial.clientId, name: initial.clientName } as Client)
          : undefined,
      selectedContact:
        initial?.userId && initial.userName
          ? ({ id: initial.userId, fullName: initial.userName } as UserEntity)
          : undefined,
      selectedTask:
        initial?.taskId && initial.taskName
          ? ({ id: initial.taskId, name: initial.taskName } as Task)
          : undefined,
      toEmailsText: (initial?.toEmails ?? []).join(', '),
      subject,
    });
    setSendError(null);
    setDraftError(null);
    window.setTimeout(() => clientInputRef.current?.focus(), 50);
  }, [initial, open]);

  const aliasesQuery = useQuery({
    queryKey: ['communications', 'gmail-send-as'],
    queryFn: () => communicationsApi.sendAs(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const aliases = aliasesQuery.data?.aliases ?? [];
    if (!open || state.fromEmail || aliases.length === 0) return;
    setState((prev) => ({
      ...prev,
      fromEmail: aliases.find((alias) => alias.isDefault)?.email ?? aliases[0]?.email ?? '',
    }));
  }, [aliasesQuery.data?.aliases, open, state.fromEmail]);

  const clientsQuery = useQuery({
    queryKey: ['email-composer', 'clients', state.clientSearch],
    queryFn: () => clientsApi.list({ q: state.clientSearch.trim() || undefined }),
    enabled: open && state.clientSearch.trim().length > 1,
    staleTime: 60_000,
  });
  const contactsQuery = useQuery({
    queryKey: ['email-composer', 'contacts', state.contactSearch, state.selectedClient?.id],
    queryFn: () =>
      usersApi.list({
        q: state.contactSearch.trim() || undefined,
        clientId: state.selectedClient?.id,
      }),
    enabled: open && (state.contactSearch.trim().length > 1 || Boolean(state.selectedClient?.id)),
    staleTime: 60_000,
  });
  const tasksQuery = useQuery({
    queryKey: ['email-composer', 'tasks', state.selectedClient?.id, state.taskSearch],
    queryFn: () =>
      tasksApi.list({
        clientId: state.selectedClient?.id,
        search: state.taskSearch.trim() || undefined,
        includeClosed: false,
        limit: 25,
      }),
    enabled: open && (Boolean(state.selectedClient?.id) || state.taskSearch.trim().length > 1),
    staleTime: 60_000,
  });
  const websitesQuery = useQuery({
    queryKey: ['email-composer', 'websites', state.selectedClient?.id, state.websiteSearch],
    queryFn: () =>
      websitesApi.list({
        clientId: state.selectedClient?.id,
        q: state.websiteSearch.trim() || undefined,
      }),
    enabled: open && (Boolean(state.selectedClient?.id) || state.websiteSearch.trim().length > 1),
    staleTime: 60_000,
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      communicationsApi.sendGmail({
        fromEmail: state.fromEmail,
        toEmails: splitEmails(state.toEmailsText),
        ccEmails: splitEmails(state.ccEmailsText),
        subject: state.subject,
        bodyMarkdown: state.bodyMarkdown,
        clientId: state.selectedClient?.id ?? initial?.clientId,
        userId: state.selectedContact?.id ?? initial?.userId,
        taskId: state.selectedTask?.id ?? initial?.taskId,
        websiteId: state.selectedWebsite?.id ?? initial?.websiteId,
        replyToRecordId: initial?.replyToRecordId,
        threadId: initial?.threadId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['communications'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      setNotice(initial?.mode === 'reply' ? 'Reply sent' : 'Email sent');
      onOpenChange(false);
    },
    onError: (error) => setSendError(error instanceof Error ? error.message : 'Email send failed.'),
  });

  const draftMutation = useMutation({
    mutationFn: () =>
      communicationsApi.draftGmail({
        instruction: state.aiInstruction,
        clientName: state.selectedClient?.name ?? initial?.clientName,
        contactEmails: splitEmails(state.toEmailsText),
        taskName: state.selectedTask?.name ?? initial?.taskName,
        threadSubject: initial?.subject,
      }),
    onSuccess: (draft) => {
      setState((prev) => ({
        ...prev,
        subject: draft.subject || prev.subject,
        bodyMarkdown: draft.bodyMarkdown || prev.bodyMarkdown,
        toEmailsText: draft.toEmails.length ? draft.toEmails.join(', ') : prev.toEmailsText,
      }));
      setDraftError(null);
    },
    onError: (error) => setDraftError(error instanceof Error ? error.message : 'AI draft failed.'),
  });

  const aliases = aliasesQuery.data?.aliases ?? [];
  const canSend = Boolean(
    state.fromEmail.trim() &&
    state.subject.trim() &&
    state.bodyMarkdown.trim() &&
    splitEmails(state.toEmailsText).length > 0,
  );
  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!canSend || sendMutation.isPending) return;
    sendMutation.mutate();
  };

  const addRecipient = (email: string | undefined) => {
    if (!email) return;
    setState((prev) => ({
      ...prev,
      toEmailsText: mergeEmails(prev.toEmailsText, email),
    }));
  };

  const startVoiceInstruction = () => {
    const SpeechRecognition = getSpeechRecognitionCtor();
    if (!SpeechRecognition) {
      setDraftError('Voice input is not available in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onstart = () => setListening(true);
    recognition.onerror = () => {
      setListening(false);
      setDraftError('Voice input failed.');
    };
    recognition.onend = () => setListening(false);
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript)
        .filter(Boolean)
        .join(' ')
        .trim();
      if (transcript) {
        setState((prev) => ({
          ...prev,
          aiInstruction: [prev.aiInstruction, transcript].filter(Boolean).join(' '),
        }));
      }
    };
    recognition.start();
  };

  return (
    <FormDrawer
      open={open}
      title={initial?.mode === 'reply' ? 'Reply in Gmail Thread' : 'Send Email'}
      description={state.selectedClient?.name ?? initial?.clientName ?? 'One Up Gmail'}
      icon={Mail}
      widthClassName="sm:max-w-[720px] md:max-w-[840px]"
      submitLabel={initial?.mode === 'reply' ? 'Send Reply' : 'Send Email'}
      pendingLabel="Sending..."
      submitHint="Cmd/Ctrl+Enter to send"
      submitIcon={Send}
      canSubmit={canSend && !sendMutation.isPending}
      pending={sendMutation.isPending}
      onClose={() => onOpenChange(false)}
      onSubmit={submit}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <ComposerPicker
          label="Client"
          inputRef={clientInputRef}
          value={state.clientSearch}
          selected={state.selectedClient?.name}
          selectedValue={state.selectedClient?.name}
          placeholder="Search clients"
          options={clientsQuery.data?.items ?? []}
          getKey={(client) => client.id}
          getLabel={(client) => client.name}
          getMeta={(client) => client.status}
          onChange={(value) =>
            setState((prev) => ({
              ...prev,
              clientSearch: value,
              selectedClient: value.trim() ? prev.selectedClient : undefined,
            }))
          }
          onSelect={(client) =>
            setState((prev) => ({
              ...prev,
              selectedClient: client,
              clientSearch: client.name,
              selectedTask:
                prev.selectedTask?.client?.id === client.id ? prev.selectedTask : undefined,
              selectedWebsite:
                prev.selectedWebsite?.organizationId === client.id
                  ? prev.selectedWebsite
                  : undefined,
            }))
          }
        />
        <ComposerPicker
          label="Contact"
          value={state.contactSearch}
          selected={state.selectedContact?.fullName}
          selectedValue={state.selectedContact?.email ?? state.selectedContact?.fullName}
          placeholder="Search contacts"
          options={contactsQuery.data?.items ?? []}
          getKey={(contact) => contact.id}
          getLabel={(contact) => contact.fullName}
          getMeta={(contact) => contact.email}
          onChange={(value) =>
            setState((prev) => ({
              ...prev,
              contactSearch: value,
              selectedContact: value.trim() ? prev.selectedContact : undefined,
            }))
          }
          onSelect={(contact) => {
            setState((prev) => ({
              ...prev,
              selectedContact: contact,
              contactSearch: contact.email ?? contact.fullName,
              toEmailsText: contact.email
                ? mergeEmails(prev.toEmailsText, contact.email)
                : prev.toEmailsText,
            }));
          }}
        />
      </div>

      {(contactsQuery.data?.items ?? []).some((contact) => contact.email) && (
        <div className="flex flex-wrap gap-1.5">
          {(contactsQuery.data?.items ?? []).slice(0, 8).map((contact) =>
            contact.email ? (
              <Button
                key={contact.id}
                type="button"
                size="sm"
                variant={
                  splitEmails(state.toEmailsText).includes(contact.email.toLowerCase())
                    ? 'secondary'
                    : 'outline'
                }
                className="h-7 max-w-full gap-1.5 px-2 text-[11px]"
                onClick={() => addRecipient(contact.email)}
              >
                <span className="truncate">{contact.fullName}</span>
                <span className="truncate font-normal opacity-70">{contact.email}</span>
              </Button>
            ) : null,
          )}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <ComposerPicker
          label="Task"
          value={state.taskSearch}
          selected={state.selectedTask?.name}
          selectedValue={state.selectedTask?.name}
          placeholder="Search open tasks"
          options={tasksQuery.data?.items ?? []}
          getKey={(task) => task.id}
          getLabel={(task) => task.name}
          getMeta={(task) => task.client?.name ?? task.status}
          onChange={(value) =>
            setState((prev) => ({
              ...prev,
              taskSearch: value,
              selectedTask: value.trim() ? prev.selectedTask : undefined,
            }))
          }
          onSelect={(task) =>
            setState((prev) => ({
              ...prev,
              selectedTask: task,
              taskSearch: task.name,
              selectedClient: prev.selectedClient ?? (task.client as Client | undefined),
              clientSearch: prev.clientSearch || task.client?.name || '',
            }))
          }
        />
        <ComposerPicker
          label="Website"
          value={state.websiteSearch}
          selected={state.selectedWebsite?.friendlyName ?? state.selectedWebsite?.url}
          selectedValue={state.selectedWebsite?.friendlyName ?? state.selectedWebsite?.url}
          placeholder="Search websites"
          options={websitesQuery.data?.items ?? []}
          getKey={(website) => website.id}
          getLabel={(website) => website.friendlyName ?? website.url}
          getMeta={(website) => website.organizationName ?? website.status}
          onChange={(value) =>
            setState((prev) => ({
              ...prev,
              websiteSearch: value,
              selectedWebsite: value.trim() ? prev.selectedWebsite : undefined,
            }))
          }
          onSelect={(website) =>
            setState((prev) => ({
              ...prev,
              selectedWebsite: website,
              websiteSearch: website.friendlyName ?? website.url,
            }))
          }
        />
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <Field label="From">
          <Select
            value={state.fromEmail}
            onValueChange={(fromEmail) => setState((prev) => ({ ...prev, fromEmail }))}
            disabled={aliases.length === 0}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  aliasesQuery.isLoading
                    ? 'Loading aliases'
                    : aliases.length === 0
                      ? 'No Gmail sender'
                      : 'Sender'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {aliases.map((alias) => (
                <SelectItem key={alias.email} value={alias.email}>
                  {formatAlias(alias)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {aliases.length === 0 && !aliasesQuery.isLoading && (
            <p className="text-[11px] text-muted-foreground">
              Connect Gmail in Settings to send email from this form.
            </p>
          )}
          {aliasesQuery.error && (
            <p className="text-[11px] text-destructive">
              {aliasesQuery.error instanceof Error
                ? aliasesQuery.error.message
                : 'Could not load Gmail senders.'}
            </p>
          )}
        </Field>
        <Field label="To">
          <Input
            value={state.toEmailsText}
            onChange={(event) =>
              setState((prev) => ({ ...prev, toEmailsText: event.target.value }))
            }
            placeholder="client@example.com, teammate@example.com"
          />
        </Field>
      </div>

      <Field label="CC">
        <Input
          value={state.ccEmailsText}
          onChange={(event) => setState((prev) => ({ ...prev, ccEmailsText: event.target.value }))}
          placeholder="optional@example.com"
        />
      </Field>

      <Field label="Subject">
        <Input
          value={state.subject}
          onChange={(event) => setState((prev) => ({ ...prev, subject: event.target.value }))}
        />
      </Field>

      <Field label="Body">
        <textarea
          value={state.bodyMarkdown}
          onChange={(event) => setState((prev) => ({ ...prev, bodyMarkdown: event.target.value }))}
          rows={10}
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-[12px] leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </Field>

      <div className="rounded-md border bg-muted/20 p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            AI Draft
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1.5"
            onClick={startVoiceInstruction}
            disabled={listening}
          >
            <Mic className={cn('h-3.5 w-3.5', listening && 'text-primary')} aria-hidden />
            Voice
          </Button>
        </div>
        <textarea
          value={state.aiInstruction}
          onChange={(event) => setState((prev) => ({ ...prev, aiInstruction: event.target.value }))}
          rows={3}
          placeholder="Draft a quick follow-up asking for approval on the wedding page updates."
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-[13px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => draftMutation.mutate()}
            disabled={draftMutation.isPending || !state.aiInstruction.trim()}
          >
            {draftMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
            )}
            Draft
          </Button>
          {draftError && (
            <span className="inline-flex items-center gap-1 text-[12px] text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden />
              {draftError}
            </span>
          )}
        </div>
      </div>

      {sendError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{sendError}</span>
        </div>
      )}
    </FormDrawer>
  );
}

function ComposerPicker<T>({
  label,
  value,
  selected,
  selectedValue,
  placeholder,
  options,
  getKey,
  getLabel,
  getMeta,
  onChange,
  onSelect,
  inputRef,
}: {
  label: string;
  value: string;
  selected?: string;
  selectedValue?: string;
  placeholder: string;
  options: T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getMeta: (item: T) => string | undefined;
  onChange: (value: string) => void;
  onSelect: (item: T) => void;
  inputRef?: RefObject<HTMLInputElement>;
}) {
  const visibleOptions = useMemo(() => options.slice(0, 6), [options]);
  const inputMatchesSelection = Boolean(
    selected &&
    [selected, selectedValue]
      .filter((candidate): candidate is string => Boolean(candidate))
      .some((candidate) => candidate.trim().toLowerCase() === value.trim().toLowerCase()),
  );
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {selected && (
          <Badge variant="success" className="max-w-[60%] truncate">
            {selected}
          </Badge>
        )}
      </div>
      <Input
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      {visibleOptions.length > 0 && !inputMatchesSelection && (
        <div className="max-h-40 overflow-y-auto rounded-md border bg-background">
          {visibleOptions.map((option) => (
            <button
              key={getKey(option)}
              type="button"
              className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-[12px] hover:bg-muted"
              onClick={() => onSelect(option)}
            >
              <span className="min-w-0 flex-1 truncate">{getLabel(option)}</span>
              {getMeta(option) && (
                <span className="max-w-[45%] truncate text-[11px] text-muted-foreground">
                  {getMeta(option)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

function splitEmails(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[,\n;]/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}

function mergeEmails(current: string, next: string): string {
  return Array.from(
    new Set([...splitEmails(current), next.trim().toLowerCase()].filter(Boolean)),
  ).join(', ');
}

function formatAlias(alias: GmailSendAsAlias): string {
  return alias.displayName ? `${alias.displayName} <${alias.email}>` : alias.email;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  start: () => void;
}

interface SpeechRecognitionResultEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | undefined {
  const win = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return win.SpeechRecognition ?? win.webkitSpeechRecognition;
}
